import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import Breadcrumb from '@/components/layout/Breadcrumb'
import VideoFilters, { type VideoFilter } from '@/components/videos/VideoFilters'
import VideoCard from '@/components/videos/VideoCard'
import MostViewedSection from '@/components/videos/MostViewedSection'
import Input from '@/components/ui/Input'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { videosApi } from '@/services/videosApiService'
import { mockVideos } from '@/services/mockDataService'

// Helper to normalize video data (handle both camelCase and PascalCase)
const normalizeVideo = (v: any): any => ({
  id: v.id || v.Id || '',
  title: v.title || v.Title || '',
  description: v.description || v.Description || '',
  url: v.url || v.Url || '',
  thumbnailUrl: v.thumbnailUrl || v.ThumbnailUrl || '',
  topic: v.topic || v.Topic || '',
  durationSeconds: v.durationSeconds ?? v.DurationSeconds ?? 0,
  views: v.views ?? v.Views ?? 0,
  isPinned: v.isPinned ?? v.IsPinned ?? false,
  createdAt: v.createdAt || v.CreatedAt || new Date().toISOString(),
})

export default function VideosPage() {
  const { t, i18n } = useTranslation()
  const [topics, setTopics] = useState<string[]>([])
  const [selectedFilter, setSelectedFilter] = useState<VideoFilter>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [videos, setVideos] = useState<any[]>([])
  const [mostViewedVideos, setMostViewedVideos] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lang = (i18n.language || 'ar').split('-')[0]

  // Fetch distinct topics for current language
  useEffect(() => {
    videosApi.getTopics().then(setTopics).catch(() => setTopics([]))
  }, [lang])

  // Fetch most viewed videos when language changes
  useEffect(() => {
    const fetchMostViewed = async () => {
      try {
        const data = await videosApi.getMostViewed(4)
        if (data) {
          setMostViewedVideos((data || []).map(normalizeVideo))
        }
      } catch (err) {
        console.error('Failed to fetch most viewed videos:', err)
        // Fallback to mock data
        setMostViewedVideos(
          mockVideos
            .sort((a, b) => b.views - a.views)
            .slice(0, 4)
            .map((v) => normalizeVideo({
              id: v.id.toString(),
              title: v.title,
              url: '',
              thumbnailUrl: v.thumbnail,
              topic: v.topic,
              durationSeconds: 0,
              views: v.views,
              isPinned: false,
              createdAt: new Date().toISOString(),
            }))
        )
      }
    }
    fetchMostViewed()
  }, [i18n.language])

  // Fetch videos when filter, search or language changes
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const topic = selectedFilter && selectedFilter.trim() !== '' ? selectedFilter : undefined
        const data = await videosApi.getVideos(topic, searchQuery || undefined)
        if (data) {
          setVideos((data || []).map(normalizeVideo))
        }
      } catch (err) {
        console.error('Failed to fetch videos:', err)
        setError('فشل تحميل قائمة الفيديوهات')
        setVideos(
          mockVideos.map((v) => normalizeVideo({
            id: v.id.toString(),
            title: v.title,
            url: '',
            thumbnailUrl: v.thumbnail,
            topic: v.topic,
            durationSeconds: 0,
            views: v.views,
            isPinned: false,
            createdAt: new Date().toISOString(),
          }))
        )
      } finally {
        setIsLoading(false)
      }
    }
    fetchVideos()
  }, [selectedFilter, searchQuery, i18n.language])

  const filteredVideos = videos

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('common.videos') },
  ]

  return (
    <>
      <Helmet>
        <title>{t('common.videos')} - سكينة</title>
        <meta name="description" content={t('public.videos.seo.description')} />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('common.videos')}</h1>
          <p className="text-lg text-gray-600">{t('public.videos.intro')}</p>
        </div>

        {/* Most Viewed Section */}
        <MostViewedSection videos={mostViewedVideos} />

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('public.videos.searchPlaceholder')}
              className="pr-10"
            />
          </div>
          <VideoFilters topics={topics} selectedFilter={selectedFilter} onFilterChange={setSelectedFilter} />
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-gray-600">{t('public.videos.resultsCount', { count: filteredVideos.length })}</p>
        </div>

        {/* Video Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : filteredVideos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">{t('public.videos.noResults')}</p>
          </div>
        )}
      </div>
    </>
  )
}
